import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';
import PublicChrome from './public/PublicChrome';

interface Project {
  _id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  imageUrl: string;
  projectUrl: string;
  category: string;
  network: string;
  operationType: string;
  projectType?: string;
  status?: string;
  requirements?: {
    hardware: { cpu: string; ram: string; storage: string };
    recommended: { cpu: string; ram: string; storage: string };
  };
  commands: Array<{ title: string; description: string; command: string; category: string }>;
  links?: { github?: string; twitter?: string; discord?: string; website?: string };
  tags: string[];
  featured: boolean;
  categoriesContent?: Record<string, any[]>;
  categoryOrder?: string[];
  isPublished: boolean;
  createdAt: string;
}

const ProjectPage: React.FC = () => {
  const { network, slug } = useParams<{ network?: string; slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedCommand, setCopiedCommand] = useState('');
  const [activeSection, setActiveSection] = useState('Installation');
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const apiPath = network ? `/api/projects/${network}/${slug}` : `/api/projects/${slug}`;
        const response = await axios.get(apiPath);
        if (response.data.success) {
          setProject(response.data.project);
        } else {
          setError('Project not found');
        }
      } catch (err: any) {
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchProject();
  }, [slug, network]);

  useEffect(() => {
    if (project) {
      const cats = project.categoryOrder && project.categoryOrder.length > 0
        ? project.categoryOrder
        : (project.categoriesContent ? Object.keys(project.categoriesContent) : []);
      if (cats.length > 0 && !cats.includes(activeSection)) {
        setActiveSection(cats[0]);
      }
    }
  }, [project, activeSection]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCommand(id);
      setTimeout(() => setCopiedCommand(''), 2000);
    } catch (err) { console.error('Copy failed:', err); }
  };

  const handleSectionChange = (name: string) => {
    if (name !== activeSection) {
      setIsSwitching(true);
      setTimeout(() => { setActiveSection(name); setIsSwitching(false); }, 150);
    }
  };

  const renderMarkdown = (text: string) => {
    return text
      .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="pp-inline-code">$1</code>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
  };

  const highlightCode = (code: string) => {
    const lines = code.split('\n');
    return lines.map((line, li) => {
      const parts = line.split(/(\s+|[{}()[\];,=])/);
      const hl = parts.map((part, pi) => {
        const t = part.trim();
        if (!t || /^\s+$/.test(part)) return <span key={pi}>{part}</span>;
        if (['sudo','apt-get','wget','curl','bash','chmod','mkdir','cd','source','export','echo','git','docker','systemctl','mv','cp','rm','ls','cat','nano','vim'].includes(t))
          return <span key={pi} className="pp-hl-cmd">{part}</span>;
        if (t.startsWith('-')) return <span key={pi} className="pp-hl-flag">{part}</span>;
        if (t.includes('http')||t.includes('.sh')||t.includes('.json')||t.includes('.tar')||t.includes('.zip')||t.includes('.gz')||(t.includes('/')&&t.length>3))
          return <span key={pi} className="pp-hl-path">{part}</span>;
        if (t.startsWith('$')) return <span key={pi} className="pp-hl-var">{part}</span>;
        if ((t.startsWith('"')&&t.endsWith('"'))||(t.startsWith("'")&&t.endsWith("'")))
          return <span key={pi} className="pp-hl-str">{part}</span>;
        if (/^\d+$/.test(t)) return <span key={pi} className="pp-hl-num">{part}</span>;
        if (['=','{','}','(',')',']','[',';',','].includes(t)) return <span key={pi} className="pp-hl-sym">{part}</span>;
        return <span key={pi} className="pp-hl-def">{part}</span>;
      });
      return <div key={li} style={{ lineHeight: 1.7 }}>{hl}</div>;
    });
  };

  if (loading) {
    return (
      <PublicChrome>
        <div className="dusk-container py-24 text-center text-dusk-muted">Loading…</div>
      </PublicChrome>
    );
  }

  if (error || !project) {
    return (
      <PublicChrome>
        <div className="dusk-container py-24 text-center">
          <h1 className="font-display text-2xl text-dusk-ink mb-4">{error || 'Project not found'}</h1>
          <Link to="/" className="text-dusk-copper no-underline">Go back to home</Link>
        </div>
      </PublicChrome>
    );
  }

  // Use categoryOrder from admin panel to maintain correct order, including empty categories
  const categories = project.categoryOrder && project.categoryOrder.length > 0
    ? project.categoryOrder
    : (project.categoriesContent ? Object.keys(project.categoriesContent) : []);

  return (
    <PublicChrome>

{/* =========== PROJECT HERO =========== */}
      <section className="pp-hero">
        <div className="dusk-container">
          <div className="pp-hero-card">
            <div className="pp-hero-top">
              {project.imageUrl && (
                <div className="pp-hero-icon-wrap">
                  <img src={getFullImageUrl(project.imageUrl)} alt={project.title} className="pp-hero-icon" onError={handleImageError} />
                </div>
              )}
              <div className="pp-hero-info">
                <h1 className="pp-hero-title">{project.title.toUpperCase()}</h1>
                <div className="pp-hero-meta">
                  <span className="pp-badge">{project.network}</span>
                  {project.tags && project.tags.length > 0 && (
                    <div className="pp-hero-tags">
                      {project.tags.slice(0, 5).map((tag, i) => (
                        <span key={i} className="pp-tag">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Social links */}
            {project.links && (project.links.github || project.links.twitter || project.links.discord || project.links.website) && (
              <div className="pp-social-row">
                {project.links.website && (
                  <a href={project.links.website} target="_blank" rel="noopener noreferrer" className="pp-social-btn" title="Website">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  </a>
                )}
                {project.links.twitter && (
                  <a href={project.links.twitter} target="_blank" rel="noopener noreferrer" className="pp-social-btn" title="X (Twitter)">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                )}
                {project.links.discord && (
                  <a href={project.links.discord} target="_blank" rel="noopener noreferrer" className="pp-social-btn" title="Discord">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                  </a>
                )}
                {project.links.github && (
                  <a href={project.links.github} target="_blank" rel="noopener noreferrer" className="pp-social-btn" title="GitHub">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"/></svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* в•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђ CATEGORY TABS (horizontal, replaces sidebar) в•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђ */}
      {categories.length > 0 && (
        <nav className="pp-tabs-bar">
          <div className="dusk-container">
            <div className="pp-tabs">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleSectionChange(cat)}
                  className={`pp-tab ${activeSection === cat ? 'active' : ''}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </nav>
      )}

      {/* в•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђ MAIN CONTENT в•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђв•ђ */}
      <section className="pp-content-section">
        <div className="dusk-container">

          {/* System Requirements вЂ” only in Installation */}
          {project.requirements && activeSection === 'Installation' && (
            <div className="pp-card pp-req-card">
              <h2 className="pp-card-title">System Requirements</h2>
              <div className="pp-req-grid">
                {(project.requirements.hardware.cpu || project.requirements.hardware.ram || project.requirements.hardware.storage) && (
                  <div className="pp-req-block">
                    <h3 className="pp-req-label">Minimum</h3>
                    <ul className="pp-req-list">
                      {project.requirements.hardware.cpu && <li><strong>CPU:</strong> {project.requirements.hardware.cpu}</li>}
                      {project.requirements.hardware.ram && <li><strong>RAM:</strong> {project.requirements.hardware.ram}</li>}
                      {project.requirements.hardware.storage && <li><strong>Storage:</strong> {project.requirements.hardware.storage}</li>}
                    </ul>
                  </div>
                )}
                {(project.requirements.recommended.cpu || project.requirements.recommended.ram || project.requirements.recommended.storage) && (
                  <div className="pp-req-block">
                    <h3 className="pp-req-label">Recommended</h3>
                    <ul className="pp-req-list">
                      {project.requirements.recommended.cpu && <li><strong>CPU:</strong> {project.requirements.recommended.cpu}</li>}
                      {project.requirements.recommended.ram && <li><strong>RAM:</strong> {project.requirements.recommended.ram}</li>}
                      {project.requirements.recommended.storage && <li><strong>Storage:</strong> {project.requirements.recommended.storage}</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Installation guide (legacy content field) */}
          {project.content && (
            <div className="pp-card">
              <h2 className="pp-card-title">Installation Guide</h2>
              <div className="pp-prose" dangerouslySetInnerHTML={{ __html: `<div>${renderMarkdown(project.content)}</div>` }} />
            </div>
          )}

          {/* Category steps */}
          {project.categoriesContent && project.categoriesContent[activeSection] && (
            <div className={`pp-steps ${isSwitching ? 'switching' : ''}`}>
              {Array.isArray(project.categoriesContent[activeSection]) && project.categoriesContent[activeSection]
                .sort((a: any, b: any) => a.order - b.order)
                .map((step: any, index: number) => (
                  <div key={index} className="pp-card" id={step.title ? `step-${activeSection}-${index}` : undefined}>
                    {step.title && <h2 className="pp-step-title">{step.title}</h2>}

                    {step.content && (
                      <div className="pp-prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(step.content) }} />
                    )}

                    {step.commands && step.commands.length > 0 && (
                      <div className="pp-code-wrap">
                        <pre className="pp-code"><code>{highlightCode(step.commands.join('\n'))}</code></pre>
                        <button className="pp-copy-btn" onClick={() => copyToClipboard(step.commands.join('\n'), `cat-${activeSection}-${index}`)}>
                          {copiedCommand === `cat-${activeSection}-${index}` ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    )}

                    {/* Additional sections 1-3 */}
                    {[
                      { t: step.additionalTitle, c: step.additionalContent, code: step.additionalCode, id: `add-${activeSection}-${index}` },
                      { t: step.additionalTitle2, c: step.additionalContent2, code: step.additionalCode2, id: `add2-${activeSection}-${index}` },
                      { t: step.additionalTitle3, c: step.additionalContent3, code: step.additionalCode3, id: `add3-${activeSection}-${index}` },
                    ].map((extra, ei) => (
                      <React.Fragment key={ei}>
                        {extra.t && <h4 className="pp-extra-title">{extra.t}</h4>}
                        {extra.c && <div className="pp-extra-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(extra.c) }} />}
                        {extra.code && (
                          <div className="pp-code-wrap">
                            <pre className="pp-code"><code>{highlightCode(extra.code)}</code></pre>
                            <button className="pp-copy-btn" onClick={() => copyToClipboard(extra.code, extra.id)}>
                              {copiedCommand === extra.id ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ))}
            </div>
          )}

          {/* Legacy commands grid */}
          {project.commands && project.commands.length > 0 && (
            <div className="pp-card">
              <h2 className="pp-card-title">Useful Commands</h2>
              <div className="pp-cmd-grid">
                {project.commands.map((cmd, index) => (
                  <div key={index} className="pp-cmd-item">
                    <div className="pp-cmd-head">
                      <h3 className="pp-cmd-name">{cmd.title}</h3>
                      <span className="pp-cmd-cat">{cmd.category}</span>
                    </div>
                    <p className="pp-cmd-desc">{cmd.description}</p>
                    <div className="pp-code-wrap">
                      <pre className="pp-code"><code>{highlightCode(cmd.command)}</code></pre>
                      <button className="pp-copy-btn" onClick={() => copyToClipboard(cmd.command, `cmd-${index}`)}>
                        {copiedCommand === `cmd-${index}` ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>
</PublicChrome>
  );
};

export default ProjectPage;

